import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TablePagination,
  Tooltip,
  Typography
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { StyledCard, MobileCard } from "../styled";
import { parseAddressParts } from "../../../lib/address";

const COLUMNS = [
  { label: "Name", key: "name", sortable: true },
  { label: "Type", key: "type", sortable: true },
  { label: "Address", key: "address", sortable: true },
  { label: "City", key: "city", sortable: true },
  { label: "State", key: "state", sortable: true },
  { label: "Zip", key: "zip", sortable: true },
  { label: "Phone", key: "phone", sortable: false },
  { label: "Website", key: "website", sortable: false },
  { label: "Status", key: "status", sortable: true }
];

// Prefer the canonical `bar_address` (Google-enriched) parsed via the
// shared parser. Only fall back to individual OSM `addr:*` tags when
// a field is still empty after parsing. Mixing partial tags with the
// parsed address produces frankenstein rows like street="1218" with
// empty city/state/zip.
const getAddressParts = (bar) => {
  const tags = bar.osm_metadata?.all_tags || {};
  const parsed = bar.bar_address ? parseAddressParts(bar.bar_address) : {};

  let street = parsed.street || "";
  let city = parsed.city || "";
  let state = parsed.state || "";
  // Note: lib/address.js returns `postalCode`, not `zip`.
  let zip = parsed.postalCode || "";

  if (!street) {
    street = [tags["addr:housenumber"], tags["addr:street"]]
      .filter(Boolean)
      .join(" ");
  }
  if (!city) city = tags["addr:city"] || "";
  if (!state) state = tags["addr:state"] || "";
  if (!zip) zip = tags["addr:postcode"] || "";

  return { street, city, state, zip };
};

const getBarStatus = (bar, barsToExclude, conflicts, conflictResolutions) => {
  const matchedConflict = conflicts.find((c) => c.osmRecordId === bar.id);
  if (matchedConflict) {
    return (conflictResolutions?.[matchedConflict.id] || "merge") === "insert"
      ? "Keeping row despite conflict"
      : "Conflict Resolved";
  }
  return "New";
};

const getFieldValue = (bar, key, barsToExclude, conflicts, conflictResolutions) => {
  switch (key) {
    case "name": return (bar.bar_name || "").toLowerCase();
    case "type": return (bar.bar_type || bar.osm_metadata?.amenity || "").toLowerCase();
    case "address": return getAddressParts(bar).street.toLowerCase();
    case "city": return getAddressParts(bar).city.toLowerCase();
    case "state": return getAddressParts(bar).state.toLowerCase();
    case "zip": return getAddressParts(bar).zip.toLowerCase();
    case "phone": return (bar.bar_phone || "").toLowerCase();
    case "website": return (bar.bar_url || "").toLowerCase();
    case "status": return getBarStatus(bar, barsToExclude, conflicts, conflictResolutions).toLowerCase();
    default: return "";
  }
};

// Resolve a bar's display values by applying conflict resolution field selections.
// When a user picks "existing" for a field during conflict resolution, the existing
// DB value should be shown on the Push tab instead of the raw OSM value.
const resolveBarFields = (bar, conflicts, conflictResolutions, fieldSelections) => {
  const matchedConflict = conflicts.find((c) => c.osmRecordId === bar.id);
  if (!matchedConflict) return bar;
  const resolution = conflictResolutions?.[matchedConflict.id] || "merge";
  if (resolution === "insert") return bar; // Keep Both — show OSM values as-is

  const resolved = { ...bar };
  const fields = matchedConflict.fields || {};
  const selections = fieldSelections?.[matchedConflict.id] || {};

  Object.keys(fields).forEach((fieldName) => {
    const field = fields[fieldName];
    const selection = selections[fieldName];
    const value = selection === "existing" ? field.existing : field.new;
    // Map conflict field names back to the bar property names used for display
    switch (fieldName) {
      case "bar_name":
        resolved.bar_name = value;
        break;
      case "address":
        resolved.bar_address = value;
        // Clear OSM address tags so getAddressParts uses bar_address instead
        if (selection === "existing" && resolved.osm_metadata?.all_tags) {
          const cleanTags = { ...resolved.osm_metadata.all_tags };
          delete cleanTags["addr:housenumber"];
          delete cleanTags["addr:street"];
          delete cleanTags["addr:city"];
          delete cleanTags["addr:state"];
          delete cleanTags["addr:postcode"];
          resolved.osm_metadata = { ...resolved.osm_metadata, all_tags: cleanTags };
        }
        break;
      case "phone":
        resolved.bar_phone = value;
        break;
      case "website":
        resolved.bar_url = value;
        break;
      case "bar_type":
        resolved.bar_type = value;
        break;
      default:
        resolved[fieldName] = value;
        break;
    }
  });
  return resolved;
};

const BarsReviewTable = ({
  osmRawData,
  barsToExclude,
  conflicts,
  conflictResolutions,
  fieldSelections,
  onToggleBarExclusion,
  onIncludeAll,
  onExcludeAll,
  stateRegions,
  assignStateId,
  assignRegionId,
  assignSubregionId,
  onAssignStateChange,
  onAssignRegionChange,
  onAssignSubregionChange,
  disableRegionSelect,
  multipleStates,
  barsToExcludeFromRegion,
  onToggleBarRegionExclusion,
  blacklistUnincluded,
  onToggleBlacklistUnincluded,
  isMobile
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState({ field: null, direction: "asc" });

  const handleColumnSort = (key) => {
    setSort((prev) => {
      if (prev.field === key) {
        return { field: key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field: key, direction: "asc" };
    });
    setPage(0);
  };

  const sortedData = useMemo(() => {
    const list = [...osmRawData];
    if (!sort.field) {
      list.sort((a, b) => (a.bar_name || "").toLowerCase().localeCompare((b.bar_name || "").toLowerCase()));
      return list;
    }
    return list.sort((a, b) => {
      const aResolved = resolveBarFields(a, conflicts, conflictResolutions, fieldSelections);
      const bResolved = resolveBarFields(b, conflicts, conflictResolutions, fieldSelections);
      const aVal = getFieldValue(aResolved, sort.field, barsToExclude, conflicts, conflictResolutions);
      const bVal = getFieldValue(bResolved, sort.field, barsToExclude, conflicts, conflictResolutions);
      const cmp = aVal.localeCompare(bVal);
      return sort.direction === "asc" ? cmp : -cmp;
    });
  }, [osmRawData, sort, barsToExclude, conflicts, conflictResolutions, fieldSelections]);

  const excludedWithinView = useMemo(
    () => sortedData.filter((bar) => barsToExclude.has(bar.id)).length,
    [barsToExclude, sortedData]
  );

  const statusColor = (status) => {
    switch (status) {
      case "Conflict Resolved": return "#ed6c02";
      case "Keeping row despite conflict": return "#7b1fa2";
      default: return "#2e7d32";
    }
  };

  const regionName = useMemo(() => {
    if (!assignRegionId) return null;
    return stateRegions.find((s) => s.id === assignStateId)?.regions?.find((r) => r.id === assignRegionId)?.name || null;
  }, [stateRegions, assignStateId, assignRegionId]);

  const subregionName = useMemo(() => {
    if (!assignSubregionId) return null;
    return stateRegions.find((s) => s.id === assignStateId)?.regions?.find((r) => r.id === assignRegionId)?.subregions?.find((sr) => sr.id === assignSubregionId)?.name || null;
  }, [stateRegions, assignStateId, assignRegionId, assignSubregionId]);

  return (
    <>
      {/* Region assignment controls — separate from table */}
      <StyledCard sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Region Assignment</Typography>
          {multipleStates && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Multiple states detected in this dataset. You can still assign a region — use the checkboxes on each row to skip region assignment for specific bars (e.g. bars from a different state).
            </Alert>
          )}
          <Box
            display="flex"
            gap={2}
            flexWrap="wrap"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "stretch" : "center"}
          >
            <FormControl size="small" sx={{ minWidth: isMobile ? undefined : 220 }} fullWidth={isMobile}>
              <InputLabel id="assign-state-select-label">Assign State</InputLabel>
              <Select
                labelId="assign-state-select-label"
                label="Assign State"
                value={assignStateId}
                onChange={onAssignStateChange}
                disabled={disableRegionSelect}
              >
                <MenuItem value="">No state</MenuItem>
                {stateRegions
                  .filter((s) => s.name && typeof s.name === "string")
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}{s.abbr ? ` (${s.abbr})` : ""}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: isMobile ? undefined : 220 }} fullWidth={isMobile}>
              <InputLabel id="assign-region-select-label">Assign Region</InputLabel>
              <Select
                labelId="assign-region-select-label"
                label="Assign Region"
                value={assignRegionId}
                onChange={onAssignRegionChange}
                disabled={disableRegionSelect || !assignStateId}
              >
                <MenuItem value="">No region</MenuItem>
                {(stateRegions.find((s) => s.id === assignStateId)?.regions || [])
                  .filter((r) => r.name && typeof r.name === "string")
                  .map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: isMobile ? undefined : 220 }} fullWidth={isMobile}>
              <InputLabel id="assign-subregion-select-label">Assign Subregion</InputLabel>
              <Select
                labelId="assign-subregion-select-label"
                label="Assign Subregion"
                value={assignSubregionId}
                onChange={onAssignSubregionChange}
                disabled={disableRegionSelect || !assignStateId || !assignRegionId}
              >
                <MenuItem value="">No subregion</MenuItem>
                {(stateRegions
                  .find((s) => s.id === assignStateId)
                  ?.regions?.find((r) => r.id === assignRegionId)
                  ?.subregions || [])
                  .filter((sr) => sr.name && typeof sr.name === "string")
                  .map((sr) => (
                    <MenuItem key={sr.id} value={sr.id}>{sr.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            {!isMobile && <Box sx={{ flexGrow: 1, minWidth: 16 }} />}
            <Tooltip
              title="Excluded venues won't show up on future lists when adding bars. These records can be removed from the Excluded Venues tab in the admin section."
              placement="top"
            >
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={blacklistUnincluded}
                    onChange={(e) => onToggleBlacklistUnincluded?.(e.target.checked)}
                    color="primary"
                  />
                )}
                label="Exclude Unincluded Records"
              />
            </Tooltip>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Bars table — matches Step 1 styling */}
      <StyledCard>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">
              Bars to Upload ({sortedData.length - excludedWithinView} of {sortedData.length})
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              {barsToExclude.size > 0 && (
                <Button size="small" onClick={onIncludeAll} variant="outlined" color="secondary">
                  Include All
                </Button>
              )}
            </Box>
          </Box>

          {isMobile ? (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    size="small"
                    checked={excludedWithinView === 0 && sortedData.length > 0}
                    indeterminate={excludedWithinView > 0 && excludedWithinView < sortedData.length}
                    onChange={(e) => {
                      if (e.target.checked) onIncludeAll();
                      else onExcludeAll();
                    }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Include / exclude all
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={sort.field ? `${sort.field}-${sort.direction}` : ""}
                    label="Sort by"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setSort({ field: null, direction: "asc" });
                      } else {
                        const [field, dir] = val.split("-");
                        setSort({ field, direction: dir });
                      }
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {COLUMNS.filter((col) => col.sortable).map((col) => [
                      <MenuItem key={`${col.key}-asc`} value={`${col.key}-asc`}>{col.label} (A→Z)</MenuItem>,
                      <MenuItem key={`${col.key}-desc`} value={`${col.key}-desc`}>{col.label} (Z→A)</MenuItem>
                    ])}
                  </Select>
                </FormControl>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 600, overflowY: "auto" }}>
                {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rawBar) => {
                  const bar = resolveBarFields(rawBar, conflicts, conflictResolutions, fieldSelections);
                  const { street, city, state, zip } = getAddressParts(bar);
                  const isExcluded = barsToExclude.has(bar.id);
                  const status = getBarStatus(bar, barsToExclude, conflicts, conflictResolutions);
                  return (
                    <MobileCard
                      key={bar.id}
                      style={{
                        borderColor: isExcluded ? "#ef9a9a" : undefined,
                        opacity: isExcluded ? 0.55 : 1,
                        background: isExcluded ? "#fff5f5" : undefined
                      }}
                    >
                      <div className="card-header">
                        <Box display="flex" alignItems="flex-start" gap={1} flex={1}>
                          <Checkbox
                            size="small"
                            checked={!isExcluded}
                            onChange={() => onToggleBarExclusion(bar.id)}
                            sx={{ mt: -0.5, ml: -0.5 }}
                          />
                          <div>
                            <strong>{bar.bar_name || "Unnamed"}</strong>
                          </div>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.8rem", fontWeight: 500, color: statusColor(status) }}
                        >
                          {status}
                        </Typography>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Type</span>
                        <span className="card-value">{bar.bar_type || bar.osm_metadata?.amenity || "—"}</span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Address</span>
                        <span className="card-value">{street || "—"}</span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">City</span>
                        <span className="card-value">{city || "—"}</span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">State / Zip</span>
                        <span className="card-value">{[state, zip].filter(Boolean).join(" ") || "—"}</span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Phone</span>
                        <span className="card-value">{bar.bar_phone || "—"}</span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Website</span>
                        <span className="card-value">
                          {bar.bar_url ? (
                            <a
                              href={bar.bar_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: "0.8rem", wordBreak: "break-all" }}
                            >
                              {bar.bar_url.substring(0, 30)}{bar.bar_url.length > 30 ? "..." : ""}
                            </a>
                          ) : "—"}
                        </span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Region</span>
                        <span className="card-value">
                          {barsToExcludeFromRegion?.has(bar.id) ? (
                            <Tooltip title="Region assignment skipped — bar is from a different state">
                              <Typography component="span" sx={{ fontSize: "0.8rem", color: "warning.main" }}>—</Typography>
                            </Tooltip>
                          ) : (
                            <Typography component="span" sx={{ fontSize: "0.8rem", color: regionName ? "text.primary" : "text.disabled" }}>
                              {regionName || "—"}
                            </Typography>
                          )}
                        </span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Subregion</span>
                        <span className="card-value">
                          {barsToExcludeFromRegion?.has(bar.id) ? (
                            <Typography component="span" sx={{ fontSize: "0.8rem", color: "text.disabled" }}>—</Typography>
                          ) : (
                            <Typography component="span" sx={{ fontSize: "0.8rem", color: subregionName ? "text.primary" : "text.disabled" }}>
                              {subregionName || "—"}
                            </Typography>
                          )}
                        </span>
                      </div>
                      <div className="card-row">
                        <span className="card-label">Skip Region</span>
                        <span className="card-value">
                          <Tooltip title={barsToExcludeFromRegion?.has(bar.id) ? "Region assignment skipped" : "Skip region assignment for this row"}>
                            <Checkbox
                              size="small"
                              checked={barsToExcludeFromRegion?.has(bar.id) ?? false}
                              onChange={() => onToggleBarRegionExclusion?.(bar.id)}
                              sx={{ p: 0, "&.Mui-checked": { color: "warning.main" } }}
                            />
                          </Tooltip>
                        </span>
                      </div>
                    </MobileCard>
                  );
                })}
              </Box>
              <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{ "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-displayedRows": { marginLeft: "auto" } }}
              />
            </>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                position: "relative"
              }}
            >
              <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "40px 1.5fr 80px 1.2fr 100px 50px 60px 100px 1fr 110px 120px 120px 60px",
                    gap: "1px",
                    backgroundColor: "#f5f5f5"
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1,
                      backgroundColor: "white",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={excludedWithinView === 0 && sortedData.length > 0}
                      indeterminate={excludedWithinView > 0 && excludedWithinView < sortedData.length}
                      onChange={(e) => {
                        if (e.target.checked) onIncludeAll();
                        else onExcludeAll();
                      }}
                    />
                  </Box>
                  {COLUMNS.map((col) => {
                    const active = sort.field === col.key;
                    return (
                      <Box
                        key={col.key}
                        onClick={col.sortable ? () => handleColumnSort(col.key) : undefined}
                        sx={{
                          py: 0.5,
                          px: 1,
                          backgroundColor: "white",
                          fontWeight: "bold",
                          borderBottom: "2px solid #e0e0e0",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          fontSize: "0.85rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          ...(col.sortable
                            ? {
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { color: "primary.main" }
                              }
                            : {})
                        }}
                      >
                        {col.label}
                        {col.sortable && (
                          <ExpandMore
                            sx={{
                              fontSize: 18,
                              transition: "transform 0.2s, opacity 0.2s",
                              transform: active && sort.direction === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                              opacity: active ? 1 : 0.35,
                              color: active ? "primary.main" : "text.secondary"
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1,
                      backgroundColor: "white",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    Region
                  </Box>
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1,
                      backgroundColor: "white",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    Subregion
                  </Box>
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1,
                      backgroundColor: "white",
                      fontWeight: "bold",
                      borderBottom: "2px solid #e0e0e0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <Tooltip title="Check to skip region assignment for this row">
                      <span>Skip Region</span>
                    </Tooltip>
                  </Box>

                  {/* Rows */}
                  {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rawBar) => {
                    const bar = resolveBarFields(rawBar, conflicts, conflictResolutions, fieldSelections);
                    const { street, city, state, zip } = getAddressParts(bar);
                    const isExcluded = barsToExclude.has(bar.id);
                    const status = getBarStatus(bar, barsToExclude, conflicts, conflictResolutions);

                    const cellSx = {
                      py: 0.5,
                      px: 1,
                      backgroundColor: isExcluded ? "#ffebee" : "white",
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      opacity: isExcluded ? 0.6 : 1
                    };

                    return (
                      <React.Fragment key={bar.id}>
                        <Box sx={{ ...cellSx, justifyContent: "center" }}>
                          <Checkbox
                            size="small"
                            checked={!isExcluded}
                            onChange={() => onToggleBarExclusion(bar.id)}
                          />
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: "medium" }}>
                            {bar.bar_name || "Unnamed"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                            {bar.bar_type || bar.osm_metadata?.amenity || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {street || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {city || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {state || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {zip || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {bar.bar_phone || "—"}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          {bar.bar_url ? (
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem", color: "primary.main", wordBreak: "break-all" }}
                              component="a"
                              href={bar.bar_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {bar.bar_url}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>—</Typography>
                          )}
                        </Box>
                        <Box sx={cellSx}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem", fontWeight: 500, color: statusColor(status), whiteSpace: "normal", wordBreak: "break-word" }}
                          >
                            {status}
                          </Typography>
                        </Box>
                        <Box sx={cellSx}>
                          {barsToExcludeFromRegion?.has(bar.id) ? (
                            <Tooltip title="Region assignment skipped — bar is from a different state">
                              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "warning.main" }}>—</Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: regionName ? "text.primary" : "text.disabled" }}>
                              {regionName || "—"}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={cellSx}>
                          {barsToExcludeFromRegion?.has(bar.id) ? (
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.disabled" }}>—</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: subregionName ? "text.primary" : "text.disabled" }}>
                              {subregionName || "—"}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ ...cellSx, justifyContent: "center" }}>
                          <Tooltip title={barsToExcludeFromRegion?.has(bar.id) ? "Region assignment skipped for this row" : "Skip region assignment for this row"}>
                            <Checkbox
                              size="small"
                              checked={barsToExcludeFromRegion?.has(bar.id) ?? false}
                              onChange={() => onToggleBarRegionExclusion?.(bar.id)}
                              sx={{ "&.Mui-checked": { color: "warning.main" } }}
                            />
                          </Tooltip>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
              <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{ "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-displayedRows": { marginLeft: "auto" } }}
              />
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </>
  );
};

export default BarsReviewTable;
